import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useState } from 'react'

import {
  useStocksServiceDeleteFundingSourceEndpoint,
  useStocksServiceGetListFundingSourceEndpoint,
  useStocksServiceGetListFundingSourceEndpointKey
} from 'src/api/queries'
import { ApiError, GetGroupListResponseItem } from 'src/api/requests'
import {
  DeleteConfirm,
  PageBreadcrumbs,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler, resetPagination } from 'src/utils'

import { FundingSourceModal } from '../components'

export const FundingSource = () => {
  const { t, currentLang } = useLocales()
  const queryClient = useQueryClient()

  const pagination = useTablePagination()
  const [search, setSearch] = useState<string>()

  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!search && {
      searchTerm: search
    })
  }

  const { data, isLoading } = useStocksServiceGetListFundingSourceEndpoint(
    queryParams,
    [queryParams]
  )

  const { mutate } = useStocksServiceDeleteFundingSourceEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetListFundingSourceEndpointKey]
      })
    }
  })

  const getItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      label: t('common.edit'),
      icon: <EditOutlined />,
      onClick: () => {
        setCurrentId(id)
        setIsOpen(true)
      }
    },
    {
      key: 'delete',
      danger: true,
      label: (
        <DeleteConfirm
          title={t('funding_source.delete')}
          onConfirm={() =>
            mutate({
              id: String(id)
            })
          }
        />
      )
    }
  ]

  const columns: ColumnType<GetGroupListResponseItem>[] = [
    {
      key: 'name',
      title: t('classifiers.title'),
      render: (_, { name }) => name?.[currentLang.value]
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="flex-end" items={getItems(id)} />
      )
    }
  ]

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onModalClose = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  return (
    <PageBreadcrumbs
      title={t('menu.funding_source')}
      items={[{ title: t('menu.funding_source') }]}
    >
      <Flex vertical gap={24}>
        <Flex gap={24}>
          <SearchBar
            className="full-width"
            placeholder={t('transfer.search_placeholder')}
            onSearch={onSearch}
          />

          <Button icon={<PlusOutlined />} type="primary" onClick={onToggle}>
            {t('funding_source.create')}
          </Button>
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>

      {isOpen && <FundingSourceModal onCancel={onModalClose} id={currentId} />}
    </PageBreadcrumbs>
  )
}
